/**
 * Coffee wallpapers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_wallpapers_iv`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const coffeeWallpapersPack: ToilePack = {
    slug: 'coffee-wallpapers',
    category: 'screens',
    name: 'Coffee wallpapers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 768, h: 1344},
    readings: [
        {key: 'latte', label: 'Latte art', subject: 'a latte seen from above with a heart drawn in the foam, on a saucer on a wooden table with a few beans'},
        {key: 'rain', label: 'Rainy window', subject: 'a steaming mug of coffee on a windowsill, raindrops on the glass and blurred city lights outside'},
        {key: 'beans', label: 'Coffee beans', subject: 'roasted coffee beans scattered across the lower half of the screen on a dark ground, a few with a soft shine'},
    ],
    styles: [
        {key: 'flatlay', label: 'Flat lay', style: 'overhead food photograph look, soft window light, crisp steam, a linen cloth and a wooden table', palette: 'warm browns, cream and caramel', framing: 'Full-bleed, the cup in the lower half'},
        {key: 'sketch', label: 'Ink sketch', style: 'loose pen and wash sketch of the scene, coffee-stain washes, hand-drawn lines on paper', palette: 'coffee brown and cream paper tones', framing: 'Full-bleed, the subject in the lower half with paper space above'},
        {key: 'retro', label: 'Retro cafe', style: 'retro cafe illustration, flat shapes, grain texture, a seventies poster feel', palette: 'burnt orange, brown and cream', framing: 'Full-bleed, the subject in the lower half with a plain cream sky above'},
    ],
    directions: ['iced coffee', 'croissant', 'coffee shop'],
    directionHint: 'try: iced coffee',
    seo: {
        title: 'Coffee Wallpapers: Latte Art, Rainy Window, Beans',
        metaDescription: 'Coffee phone wallpapers in warm browns and cream: latte art from above, a steaming mug by a rainy window and roasted beans, in three styles.',
        h1: 'Coffee wallpapers: latte art, a rainy window, coffee beans',
        tagline: 'Nine phone wallpapers in warm browns and cream, three coffee scenes in three styles.',
        intro: 'Phone wallpapers of coffee: a latte seen from above with a heart in the foam, a steaming mug by a rainy window, and roasted beans on a dark ground. Each comes as an overhead food photo look, a loose ink sketch and a retro cafe poster. Coffee lovers can set them as they are. Type a different subject, such as iced coffee or a croissant, and the styles carry it.',
        useCases: [
            'a phone lock screen',
            'a cafe or kitchen print',
            'a gift for a coffee lover',
            'a tablet background',
        ],
        faq: [
            {q: 'What are the nine pictures?', a: 'Three coffee scenes: a latte with a heart in the foam, a mug by a rainy window, and a scatter of roasted beans. Each scene is shown in three styles.'},
            {q: 'Can I change the subject?', a: 'Yes. Type a subject such as iced coffee, a croissant or a coffee shop; a typed subject is a request, not a promised result.'},
            {q: 'How do the three styles differ?', a: 'Flat lay is an overhead food photo look on linen and wood. Ink sketch uses pen lines and coffee-stain washes. Retro cafe is a flat poster in burnt orange and brown.'},
            {q: 'Do the pictures have any text?', a: 'No. There is no text or lettering on any of the nine pictures.'},
        ],
    },
}
